/**
 * Centralized copy for the Member Security tab (consolidated password management
 * inside Customer Information). Kept out of the component per the repo rule that
 * feature text is not hardcoded inline (mirrors RoleManagement/strings.ts).
 */
export const memberSecurityStrings = {
  /** Fallback shown for an empty value. */
  none: "—",
  reveal: {
    heading: "Current password",
    button: "Reveal password",
    show: "Show password",
    hide: "Hide password",
    copyTooltip: "Copy password",
    autoGeneratedNote: "Looks auto-generated",
    copied: "Password copied to clipboard",
    error: "Could not reveal the password. Please try again.",
    // Only for a DS that predates the vault and still answers 410.
    retired:
      "The password reveal has been retired. If the member cannot sign in, set a new password below.",
    unavailable:
      "The password reveal is not available: the password vault is not configured. If the member cannot sign in, set a new password below.",
    notStoredYet: "No stored password yet.",
    storedCapturedOn: "Stored copy captured on {date}.",
    staleBadge: "May be outdated",
    staleCaveat:
      "This password was captured on {date}, before the member's last password change. It may not work.",
    capturedFrom: "Captured on {date} from {source}.",
    unknownDate: "an unknown date",
    autoHideNote: "Hidden again after 60 seconds.",
    reasonLabel: "Reason (optional)",
    reasonPlaceholder: "Why do you need this password?",
    reasonCounter: "{count}/{max}",
    noOperator:
      "Your operator e-mail could not be resolved, so the reveal is disabled. Sign out and sign in again.",
    noOperatorToken:
      "Your sign-in could not be read, so the reveal is disabled. Please sign in again to reveal passwords.",
    signInAgain: "Sign in again",
    captureSources: {
      "abo-set": "a password set in ABO",
      "hubspot-route": "a HubSpot password update",
      "member-route": "a member password change",
      "bba-set": "a book builder password change",
      provisioning: "account provisioning",
      "rmm-provisioning": "RMM provisioning",
      upsell: "an upsell purchase",
      login: "a member sign-in",
      migration: "the HubSpot migration",
    } as Record<string, string>,
    refusals: {
      PASSWORD_REVEAL_OPERATOR_UNVERIFIED:
        "Please sign in again to reveal passwords.",
      PASSWORD_NOT_STORED:
        "No password is stored for this member yet. Set a new password below instead.",
      MEMBER_NOT_FOUND: "This member was not found in DigitalServices.",
      PASSWORD_REVEAL_OPERATOR_NOT_ADMIN:
        "Only a DigitalServices administrator can reveal a member's password. Ask for the admin role, or set a new password below.",
      PASSWORD_REVEAL_TARGET_EXCLUDED:
        "The password of a staff account is never revealed.",
      rateLimitedOperator:
        "You reached your limit of password reveals for this hour. Try again later.",
      rateLimitedTarget:
        "This member's password was revealed too many times in the last hour. Try again later.",
      PASSWORD_VAULT_UNAVAILABLE:
        "The password vault is not available right now. Try again later, or set a new password below.",
      PASSWORD_REVEAL_AUDIT_FAILED:
        "The reveal could not be recorded in the audit log, so the password was not shown. Try again.",
      PASSWORD_VAULT_OPEN_FAILED:
        "The stored password could not be opened. Set a new password below instead.",
    },
  },
  set: {
    heading: "Set new password",
    inputLabel: "New Password",
    placeholder: "Enter new password",
    submit: "Save New Password",
    disabledHint: "Enter a password that meets all requirements to enable.",
    policy: {
      min: "8 characters minimum",
      upper: "One uppercase letter",
      lower: "One lowercase letter",
      numberOrSymbol: "Contains a number or symbol",
    },
    confirmTitle: "Change this member's password?",
    confirmBody:
      "You are about to set a new password for {name} ({email}). This takes effect immediately.",
    confirmYes: "Yes, change it",
    confirmNo: "Cancel",
    success: "Password changed for {email}.",
    error: "Could not change the password for {email}. Please try again.",
  },
  inactive: "This member is not active — password actions are disabled.",
} as const;

/**
 * Replaces `{token}` placeholders with provided values; unknown tokens are left
 * intact so missing interpolation is visible.
 */
export const formatMessage = (
  template: string,
  vars: Record<string, string> = {}
): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
  );
